import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { SERVICES } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, TrendUp, TrendDown } from "@phosphor-icons/react";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-900 border-yellow-400",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-500",
  completed: "bg-blue-100 text-blue-900 border-blue-500",
  cancelled: "bg-neutral-100 text-neutral-900 border-neutral-400",
  rejected: "bg-red-100 text-red-900 border-red-500",
};

export default function Dashboard() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [], earn_rate: 0.05 });
  const [eligibility, setEligibility] = useState({ eligible: [], already_reviewed: [] });
  const [review, setReview] = useState({ service: "", rating: 5, comment: "" });

  useEffect(() => {
    if (ready && !user) nav("/login", { state: { from: "/dashboard" } });
  }, [ready, user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, w, e] = await Promise.all([
        api.get("/bookings/mine"),
        api.get("/wallet"),
        api.get("/reviews/eligibility"),
      ]);
      setBookings(b.data);
      setWallet(w.data);
      setEligibility(e.data);
      // Pre-select first reviewable service
      const first = (e.data.eligible || []).find((s) => !e.data.already_reviewed.includes(s));
      if (first) setReview((r) => ({ ...r, service: first }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", review);
      toast.success("Review submitted");
      setReview({ service: "", rating: 5, comment: "" });
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not submit review";
      toast.error(typeof msg === "string" ? msg : "Could not submit review");
    }
  };

  if (!user) return null;

  const total = bookings.length;
  const pending = bookings.filter((b) => b.status === "pending").length;
  const completed = bookings.filter((b) => b.status === "completed").length;
  const totalSpend = bookings
    .filter((b) => ["approved", "completed"].includes(b.status))
    .reduce((a, b) => a + (b.amount || 0), 0);

  const reviewableServices = (eligibility.eligible || []).filter(
    (s) => !(eligibility.already_reviewed || []).includes(s)
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="label-eyebrow">My Dashboard</p>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-none mt-3">
            Hello, {user.name?.split(" ")[0]}.
          </h1>
          <p className="text-black/60 mt-3 text-sm">Manage your bookings, wallet credits and reviews.</p>
        </div>
        <Link to="/services" className="btn-primary" data-testid="dashboard-book-new">Book new service</Link>
      </div>

      {/* Stats + Wallet */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-10">
        <Stat label="Total bookings" value={total} testid="stat-total" className="md:col-span-2" />
        <Stat label="Pending" value={pending} testid="stat-pending" className="md:col-span-2" />
        <Stat label="Completed" value={completed} testid="stat-completed" className="md:col-span-2" />
        <Stat label="Total spend" value={`GHS ${totalSpend.toFixed(2)}`} testid="stat-spend" className="md:col-span-2" />
        <div className="md:col-span-4 border border-black bg-black text-white p-5 flex flex-col justify-between" data-testid="wallet-card">
          <div className="flex items-start justify-between">
            <p className="label-eyebrow text-white/60">Wallet balance</p>
            <Wallet size={20} className="text-white/70" />
          </div>
          <div>
            <p className="font-display text-3xl font-black mt-2" data-testid="wallet-balance">GHS {wallet.balance.toFixed(2)}</p>
            <p className="text-[11px] text-white/50 mt-1">
              Earn {(wallet.earn_rate * 100).toFixed(0)}% on every completed booking
            </p>
          </div>
        </div>
      </div>

      {/* Bookings */}
      <h2 className="font-display text-2xl font-black tracking-tight mt-12 mb-4">My bookings</h2>
      {loading ? (
        <p className="font-mono text-xs">Loading…</p>
      ) : bookings.length === 0 ? (
        <div className="border border-dashed border-black/30 p-12 text-center">
          <p className="text-black/60">No bookings yet.</p>
          <Link to="/services" className="btn-outline mt-4 inline-block">Browse services</Link>
        </div>
      ) : (
        <div className="border border-black overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-black text-white">
              <tr>
                <Th>Reference</Th>
                <Th>Service</Th>
                <Th>Slot</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-black/10" data-testid={`booking-row-${b.id}`}>
                  <Td className="font-mono">#{b.id.slice(-8).toUpperCase()}</Td>
                  <Td className="capitalize">{b.service}</Td>
                  <Td className="font-mono text-xs">
                    {b.slot_date ? `${b.slot_date}${b.slot_time ? " · " + b.slot_time : ""}` : (b.created_at?.slice(0, 10) || "—")}
                  </Td>
                  <Td>GHS {(b.amount || 0).toFixed(2)}</Td>
                  <Td>
                    <span className={`inline-block px-2 py-1 text-xs border ${STATUS_COLORS[b.status] || ""}`}>{b.status}</span>
                  </Td>
                  <Td className="text-xs">{b.payment_status}</Td>
                  <Td>
                    {b.payment_status === "pending" ? (
                      <Link to={`/payment/${b.id}`} className="underline" data-testid={`booking-pay-${b.id}`}>Pay now</Link>
                    ) : (
                      "—"
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Wallet transactions */}
      {wallet.transactions.length > 0 && (
        <>
          <h2 className="font-display text-2xl font-black tracking-tight mt-16 mb-4">Wallet activity</h2>
          <div className="border border-black overflow-x-auto" data-testid="wallet-transactions">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-black text-white">
                <tr><Th>Date</Th><Th>Type</Th><Th>Description</Th><Th>Amount</Th></tr>
              </thead>
              <tbody>
                {wallet.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-black/10">
                    <Td className="font-mono text-xs">{t.created_at?.slice(0, 10)}</Td>
                    <Td>
                      <span className="inline-flex items-center gap-1 text-xs uppercase font-bold">
                        {t.amount >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                        {t.type}
                      </span>
                    </Td>
                    <Td>{t.description}</Td>
                    <Td className={t.amount >= 0 ? "text-emerald-700 font-mono" : "text-red-700 font-mono"}>
                      {t.amount >= 0 ? "+" : ""}{t.amount.toFixed(2)} GHS
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Review form */}
      <h2 className="font-display text-2xl font-black tracking-tight mt-16 mb-4">Leave a review</h2>
      {reviewableServices.length === 0 ? (
        <div className="border border-dashed border-black/30 p-8">
          <p className="text-sm text-black/60">
            You can leave a review once one of your bookings is marked completed.
            {eligibility.already_reviewed.length > 0 && (
              <span> Already reviewed: {eligibility.already_reviewed.map((s) => SERVICES.find((x) => x.key === s)?.name || s).join(", ")}.</span>
            )}
          </p>
        </div>
      ) : (
        <form onSubmit={submitReview} className="border border-black p-6 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          <div>
            <label className="label-eyebrow block mb-2">Service</label>
            <select
              className="input-sharp"
              value={review.service}
              onChange={(e) => setReview({ ...review, service: e.target.value })}
              data-testid="review-service"
              required
            >
              <option value="">— Select —</option>
              {reviewableServices.map((s) => (
                <option key={s} value={s}>{SERVICES.find((x) => x.key === s)?.name || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Rating</label>
            <select
              className="input-sharp"
              value={review.rating}
              onChange={(e) => setReview({ ...review, rating: parseInt(e.target.value) })}
              data-testid="review-rating"
            >
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Comment</label>
            <input
              className="input-sharp"
              value={review.comment}
              onChange={(e) => setReview({ ...review, comment: e.target.value })}
              data-testid="review-comment"
            />
          </div>
          <div className="md:col-span-3">
            <button className="btn-primary" data-testid="review-submit">Submit review</button>
          </div>
        </form>
      )}
    </div>
  );
}

function Stat({ label, value, testid, className = "" }) {
  return (
    <div className={`border border-black p-5 ${className}`} data-testid={testid}>
      <p className="label-eyebrow text-black/50">{label}</p>
      <p className="font-display text-3xl font-black mt-2">{value}</p>
    </div>
  );
}
const Th = ({ children }) => <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`p-3 ${className}`}>{children}</td>;
